import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Imagem de produto com fallback gracioso.
 *
 * Faz `<img>` apontando para `/produtos/:codpro/imagem/:ordem` (proxy do backend).
 * Se a imagem não existir, vira um placeholder (ícone de pacote).
 *
 * Props:
 *   - codpro: string (obrigatório)
 *   - ordem: número (1 = primeira foto; default 1)
 *   - tamanho: 'sm' (40px) | 'md' (64px, default) | 'lg' (96px) | 'xl' (140px) | número
 *   - clicavel: boolean — se true, click amplia em modal (default false)
 *   - alt: string (default = "Foto produto X")
 *   - className: string extra
 *
 * Uso:
 *   <ImagemProduto codpro="28260" tamanho="md" clicavel alt="Piso Embramaco" />
 */
export default function ImagemProduto({
  codpro,
  ordem = 1,
  tamanho = 'md',
  clicavel = false,
  alt,
  className = '',
}) {
  const { apiUrl } = useAuth();
  const [erro, setErro] = useState(false);
  const [ampliado, setAmpliado] = useState(false);

  const tamanhoPx = typeof tamanho === 'number'
    ? tamanho
    : { sm: 40, md: 64, lg: 96, xl: 140 }[tamanho] || 64;

  // Endpoint público (sem token) — imagens de produto não são dado sensível.
  // Backend faz proxy + cache aggressive (30 dias).
  const src = codpro ? `${apiUrl}/produtos/${codpro}/imagem/${ordem}` : null;

  // Sem codpro OU erro → placeholder
  if (!codpro || erro || !src) {
    return (
      <div
        className={className}
        style={{
          width: tamanhoPx,
          height: tamanhoPx,
          borderRadius: 8,
          background: 'var(--primary-light)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--primary)',
          fontSize: tamanhoPx * 0.45,
          flexShrink: 0,
        }}
        title={alt || 'Sem foto disponível'}
        aria-label="Sem foto disponível"
      >
        <i className="bi bi-box-seam"></i>
      </div>
    );
  }

  return (
    <>
      <img
        src={src}
        alt={alt || `Foto do produto ${codpro}`}
        loading="lazy"
        onError={() => setErro(true)}
        onClick={clicavel ? () => setAmpliado(true) : undefined}
        className={className}
        style={{
          width: tamanhoPx,
          height: tamanhoPx,
          objectFit: 'cover',
          borderRadius: 8,
          background: '#f5f9f5',
          cursor: clicavel ? 'zoom-in' : 'default',
          flexShrink: 0,
          transition: 'transform 0.15s',
        }}
      />

      {/* Modal de ampliação — via portal no body pra escapar de ancestrais com
          transform (cards com hover/animação), senão o position:fixed fica preso */}
      {clicavel && ampliado && createPortal(
        <div
          onClick={() => setAmpliado(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: 24,
          }}
          role="dialog"
          aria-label="Imagem ampliada"
        >
          <img
            src={src}
            alt={alt || `Foto do produto ${codpro}`}
            style={{
              maxWidth: '95vw',
              maxHeight: '90vh',
              borderRadius: 12,
              boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setAmpliado(false)}
            aria-label="Fechar"
            style={{
              position: 'absolute',
              top: 20,
              right: 24,
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 44,
              height: 44,
              fontSize: '1.4rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>,
        document.body
      )}
    </>
  );
}
